import { Component, ElementRef, Input, ViewChild, AfterViewInit, ChangeDetectorRef, SecurityContext } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { StrHlp } from 'src/lib/fliko/helpers/string.helper';
import { StringHelper } from 'src/app/shared/helpers/string.helper';
import { LocLabel } from '../../clickable-row-table/models/data-table-config.models';
import { LegacyLabel } from '../common/legacy-label';

@Component({
  selector: 'app-text-input',
  templateUrl: './text-input.component.html',
  styleUrls: ['./text-input.component.scss']
})
export class TextInputComponent extends LegacyLabel implements ControlValueAccessor, AfterViewInit {
  @Input() disabled = false;
  @Input() autoFocus = false;
  @Input() placeholderKey: string | LocLabel | null | undefined;
  @Input() type: 'text' | 'number' | 'textarea' | 'email' = 'text';
  @Input() step!: string;
  @Input() default!: any;
  @Input() min!: string;
  @Input() max!: string;
  @Input() rows = 3;
  @Input() readonly = false;
  @Input() outsideLabel = true;
  @Input() required = false;
  @Input() footer = true;
  @Input() process?: (x: any) => any;
  @Input() suffix?: string;

  private _value: any;

  public onChange: any = () => {};
  public onTouch: any = () => {};

  @ViewChild('inputArea') private inputAreaRef?: ElementRef<HTMLInputElement>;

  constructor(
    public ngControl: NgControl,
    private changeDetector: ChangeDetectorRef,
    private sanitizer: DomSanitizer
  ) {
    super();
    ngControl.valueAccessor = this;
  }

  ngAfterViewInit(): void {
    if (this.autoFocus) {
      // must be in timeout, to omit ExpressionChangedAfterItHasBeenCheckedError
      setTimeout(() => this.inputAreaRef?.nativeElement.focus(), 0);
    }
  }

  // this method sets the value programmatically
  public writeValue(val: any): void {
    val = StringHelper.processValueToEntity(this.sanitizer.sanitize(SecurityContext.HTML, val) ?? '');
    if (this.process) {
      this._value = val; // HACK: force repaint
      this.changeDetector.detectChanges();
      val = this.process(val);
    }
    this._value = val;
    this.onChange(val);
  }

  get value(): any {
    return this._value;
  }

  set value(val: any) {
    this.writeValue(val);
    this.onTouch(val);
  }

  // upon UI element value changes, this method gets triggered
  public registerOnChange(fn: any) {
    this.onChange = fn;
  }

  // upon touching the element, this method gets triggered
  public registerOnTouched(fn: any) {
    this.onTouch = fn;
  }

  public focusOnInputArea(): void {
    this.inputAreaRef?.nativeElement.focus();
  }

  /** @return {number} Position of selection start or carriage when there is no selection in text */
  public get selectionStart(): number {
    return this.inputAreaRef?.nativeElement.selectionStart ?? 0;
  }

  /** @return {number} Position of carriage at selection end */
  public get selectionEnd(): number {
    return this.inputAreaRef?.nativeElement.selectionEnd ?? 0;
  }

  public insertAt(value: string, index: number): void {
    this.focusOnInputArea();

    const i = index < 0 ? 0 : index;
    const str = this._value.toString();
    const newVal = StrHlp.insert(str, i, value);
    this.value = newVal;

    // FIXME: something is happening after set value,
    // because even if we reset carriage position, it moves to the end of text input
    this.setCarriagePos(index + 1);
  }

  public insertAtCarriage(value: string): void {
    this.insertAt(value, this.selectionStart);
  }

  public reset(): void {
    this.value = this.default;
  }

  /**
   * Update carriage position in input area to given value
   * @param {number} pos
   */
  private setCarriagePos(pos: number): void {
    if (this.inputAreaRef) {
      const el = this.inputAreaRef.nativeElement;
      el.selectionEnd = el.selectionStart = pos;
    }
  }
}
